import {
  AFFECTED_OBJECT_DECISION,
  ASSESSMENT_VALUE_SOURCE,
  CONFIRMATION_STATE,
  SECTION_STATUS,
  TAffectedObjectDecision,
  TAssessmentSummary,
  TAssessmentValueSource,
  TChangeAssessment,
  TConfirmationState,
  TFieldValue,
  TSectionStatus,
  TTemplateInputValue,
} from '../models';

// Author-friendly shape for seeding one Change's assessment record from the org
// mock config (organization.mock.changeV2.assessment). The store instantiates the
// full snapshot from the selected template, then applies these overrides so the
// demo Change shows realistic answers, decisions and a decision-ready summary.
// It never authors the whole snapshot by hand; the template stays the single
// source of structure.
export type TAssessmentValueOverride = {
  value: TFieldValue;
  source?: string;
  confirmationState?: string;
  rationale?: string;
  comment?: string;
};

export type TChangeV2AssessmentConfig = {
  /** displayId of the Change this assessment belongs to (e.g. "CHG-2026-0042"). */
  changeDisplayId: string;
  /** Facts captured when the sample Change instantiated its template. */
  templateInputValues?: Record<string, TTemplateInputValue>;
  answers?: Record<string, TAssessmentValueOverride>;
  customFieldValues?: Record<string, TAssessmentValueOverride>;
  /** sectionId -> status ("Complete", "In progress", ...). */
  sectionProgress?: Record<string, string>;
  affectedObjects?: { objectId: string; decision: string; rationale?: string }[];
  summary?: TAssessmentSummary;
};

const matchEnum = <T extends string>(values: readonly T[], value: string | undefined): T | undefined =>
  value ? values.find((candidate) => candidate.toLocaleLowerCase() === value.toLocaleLowerCase()) : undefined;

const coerceSource = (value: string | undefined, fallback: TAssessmentValueSource): TAssessmentValueSource =>
  matchEnum(Object.values(ASSESSMENT_VALUE_SOURCE), value) ?? fallback;

const coerceConfirmation = (value: string | undefined, fallback: TConfirmationState): TConfirmationState =>
  matchEnum(Object.values(CONFIRMATION_STATE), value) ?? fallback;

const coerceStatus = (value: string | undefined): TSectionStatus | undefined =>
  matchEnum(Object.values(SECTION_STATUS), value);

const coerceDecision = (value: string | undefined): TAffectedObjectDecision =>
  matchEnum(Object.values(AFFECTED_OBJECT_DECISION), value) ?? AFFECTED_OBJECT_DECISION.NOT_ASSESSED;

// Applies config overrides onto a freshly instantiated assessment. Missing keys
// keep their instantiated (template-derived) values.
export const applyAssessmentConfig = (
  base: TChangeAssessment,
  config: TChangeV2AssessmentConfig,
  actor: string,
  at: string
): TChangeAssessment => {
  const answers = { ...base.answers };
  Object.entries(config.answers ?? {}).forEach(([id, override]) => {
    const existing = answers[id];
    if (!existing) return;
    answers[id] = {
      ...existing,
      value: override.value,
      source: coerceSource(override.source, existing.source),
      confirmationState: coerceConfirmation(override.confirmationState, existing.confirmationState),
      rationale: override.rationale ?? existing.rationale,
      comment: override.comment ?? existing.comment,
      lastUpdatedBy: actor,
      lastUpdatedAt: at,
    };
  });

  const customFieldValues = { ...base.customFieldValues };
  Object.entries(config.customFieldValues ?? {}).forEach(([id, override]) => {
    const existing = customFieldValues[id];
    if (!existing) return;
    customFieldValues[id] = {
      ...existing,
      value: override.value,
      source: coerceSource(override.source, existing.source),
      confirmationState: coerceConfirmation(override.confirmationState, existing.confirmationState),
      lastUpdatedBy: actor,
      lastUpdatedAt: at,
    };
  });

  const sectionProgress = { ...base.sectionProgress };
  Object.entries(config.sectionProgress ?? {}).forEach(([sectionId, status]) => {
    const nextStatus = coerceStatus(status);
    if (!nextStatus || !sectionProgress[sectionId]) return;
    sectionProgress[sectionId] = {
      sectionId,
      status: nextStatus,
      completedBy: nextStatus === SECTION_STATUS.COMPLETE ? actor : undefined,
      completedAt: nextStatus === SECTION_STATUS.COMPLETE ? at : undefined,
    };
  });

  const affectedObjects = base.affectedObjects.map((object) => {
    const override = config.affectedObjects?.find((entry) => entry.objectId === object.objectId);
    if (!override) return object;
    return {
      ...object,
      decision: coerceDecision(override.decision),
      rationale: override.rationale ?? object.rationale,
    };
  });

  return {
    ...base,
    answers,
    customFieldValues,
    sectionProgress,
    affectedObjects,
    summary: config.summary ?? base.summary,
  };
};
